import {
  FirestoreDataConverter,
  collection,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { Customer } from "@baiki/interfaces";

import { db } from "@/lib/firebase";

import { useAuth } from "./AuthProvider";

const customerConverter: FirestoreDataConverter<Customer> = {
  toFirestore: (customer) => {
    const { id: _id, ...rest } = customer;
    return rest;
  },
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)!;
    return { id: snapshot.id, ...(data as Omit<Customer, "id">) };
  },
};

type CustomerContextValue = {
  customer: Customer | null;
  loading: boolean;
};

const CustomerContext = createContext<CustomerContextValue>({
  customer: null,
  loading: true,
});

/**
 * Provides a single Firestore real-time listener for the authenticated
 * customer document. All useCustomer() calls read from this shared context,
 * guaranteeing exactly one listener per session regardless of how many
 * components call the hook.
 */
export const AppCustomerProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerLoading, setCustomerLoading] = useState(true);

  useEffect(() => {
    // Don't open a Firestore connection until auth has resolved with a user
    if (authLoading || !db || !user?.uid) return;

    const customersRef = collection(db, "customers").withConverter(
      customerConverter,
    );
    const q = query(customersRef, where("userId", "==", user.uid), limit(1));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setCustomer(snapshot.docs[0]?.data() ?? null);
        setCustomerLoading(false);
      },
      (error) => {
        if (error.code === "permission-denied") {
          console.warn(
            `[CustomerProvider] Customer data not accessible for user ${user.uid}`,
          );
          setCustomer(null);
        } else {
          console.error(
            "[CustomerProvider] Error listening to customer:",
            error,
          );
        }
        setCustomerLoading(false);
      },
    );

    return () => {
      unsubscribe();
      // Reset snapshot state so the next authenticated user starts fresh
      setCustomer(null);
      setCustomerLoading(true);
    };
  }, [user?.uid, authLoading]);

  // Derive loading: true while auth is unresolved, or while waiting for the
  // first snapshot from a newly-signed-in user's listener.
  const loading = authLoading || (!!user?.uid && customerLoading);

  return (
    <CustomerContext.Provider value={{ customer, loading }}>
      {children}
    </CustomerContext.Provider>
  );
};

/**
 * Read the current customer + auth-aware loading state from the single
 * Firestore listener mounted by AppCustomerProvider. Returns
 * `{ customer, loading }` — no extra Firestore subscriptions are
 * created regardless of how many components call this hook.
 */
export const useCustomer = () => useContext(CustomerContext);
